import castArray from 'lodash/castArray'
import { GetServerSideProps } from 'next'
import React from 'react'

import { BackToTop } from '@Components/BackToTop'
import { FooterWithGroupVisibility } from '@Components/Footer'
import { HeaderWithGroupVisibility } from '@Components/Header'
import { Page, PageType } from '@Generated/graphql'
import { initializeApolloFromContext } from '@Globals/Apollo/Server'
import { useBlockComponents } from '@Globals/CMS/useBlockComponents'
import { PageHOC } from '@Globals/Page/Client'
import { boostFromContext, handleRedirect, pageProps } from '@Globals/Page/Server'
import { PageProps } from '@Globals/Page/Types'
import { OneColumn } from '@Layouts/OneColumn'
import { getSinglePost } from '@Queries/cms/SinglePost.query'

interface CategoryProps extends PageProps {
  item: Page
}

type Params = {
  slug: string | string[]
}

export const getServerSideProps: GetServerSideProps<CategoryProps, Params> = async (context) => {
  const { params } = context
  const slug = castArray(params?.slug || [])

  if (!slug) {
    return {
      notFound: true,
    }
  }

  let categoryID: string | undefined
  if (slug.length === 1) {
    const categoryIDNumber = Number(slug[0])
    if (Number.isInteger(categoryIDNumber) && categoryIDNumber > 0 && slug[0] === categoryIDNumber.toString()) {
      // eslint-disable-next-line prefer-destructuring
      categoryID = slug[0]
    }
  }

  const url = new URL(context.req.url || '', 'http://localhost')

  const apolloClient = initializeApolloFromContext(context)
  const post = await getSinglePost(
    apolloClient,
    PageType.Category,
    url.pathname,
    categoryID,
    boostFromContext(context),
    context?.req?.thSplitTests,
  )
  const { page } = post.data

  if (!page) {
    return {
      notFound: true,
    }
  }

  if (page?.type === 'redirect_permanent' || page?.type === 'redirect_temporary') {
    return handleRedirect(context, page.url, page.type)
  }

  const { id } = page
  if (!id) {
    return {
      notFound: true,
    }
  }

  return {
    props: {
      ...await pageProps(
        context,
        {
          type: 'category',
          id,
        },
        page,
        ['cart', 'common', 'cms', 'footer', 'header', 'categories', 'sidebar', 'validation', 'shipping', 'products'],
      ),
      item: page as Page,
    },
  }
}

const Category = PageHOC<CategoryProps>(({ item }) => {
  const components = useBlockComponents(item.blocks || [])
  return (
    <>
      <OneColumn footer={<FooterWithGroupVisibility />} header={<HeaderWithGroupVisibility />}>
        {components}
      </OneColumn>
      <BackToTop />
    </>
  )
})

export default Category
